/**
 * DM Thread Item Component
 * Individual DM thread in the sidebar list
 * Supports both 1:1 and group DMs
 */

import type { DMThread } from '@/types/api.types';
import { useAppStore } from '@/store/app.store';
import { useAuthStore } from '@/store/auth.store';
import Avatar from '@/components/common/Avatar';
import { stripMentionHtml } from '@/utils/linkify.util';

interface DMThreadItemProps {
  thread: DMThread;
}

const DMThreadItem = ({ thread }: DMThreadItemProps) => {
  const { activeDMThreadId, setActiveDMThread } = useAppStore();
  const { user: currentUser } = useAuthStore();
  const isActive = activeDMThreadId === thread.id;

  const handleClick = () => {
    setActiveDMThread(thread.id);
  };

  // Get other members (exclude current user)
  const otherMembers = thread.members?.filter((m) => m.id !== currentUser?.id) || [];

  if (otherMembers.length === 0) {
    return null;
  }

  // Display name: single user for 1:1, comma-separated for groups
  const displayName = thread.is_group
    ? otherMembers.map((m) => m.display_name?.split(' ')[0] || m.email).join(', ')
    : otherMembers[0]?.display_name || otherMembers[0]?.email || 'Unknown';

  // Format last message timestamp
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours < 24) {
      return date.toLocaleTimeString('bg-BG', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } else if (diffHours < 48) {
      return 'Вчера';
    } else {
      return date.toLocaleDateString('bg-BG', {
        day: 'numeric',
        month: 'short',
      });
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`w-full flex items-center px-2 py-2 rounded-lg text-left transition-colors group ${
        isActive
          ? 'bg-blue-600 text-white'
          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
      }`}
    >
      {/* Avatar(s) */}
      <div className="flex-shrink-0 mr-3">
        {thread.is_group ? (
          <div className="relative w-8 h-8">
            {otherMembers.slice(0, 2).map((member, i) => (
              <div
                key={member.id}
                className={`absolute ${
                  i === 0 ? 'top-0 left-0' : 'bottom-0 right-0'
                }`}
              >
                <Avatar user={member} size="xs" />
              </div>
            ))}
            {otherMembers.length > 2 && (
              <div className="absolute bottom-0 right-0 w-4 h-4 bg-gray-600 rounded-full flex items-center justify-center text-[8px] text-white font-bold">
                +{otherMembers.length - 1}
              </div>
            )}
          </div>
        ) : (
          <Avatar user={otherMembers[0]} size="sm" />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{displayName}</div>
        {thread.last_message && (
          <div
            className={`text-xs truncate ${
              isActive ? 'text-blue-100' : 'text-gray-400'
            }`}
          >
            {stripMentionHtml(thread.last_message.content)}
          </div>
        )}
      </div>

      {/* Right Side */}
      <div className="flex-shrink-0 ml-2 flex flex-col items-end space-y-1">
        {thread.last_message && (
          <span
            className={`text-xs ${
              isActive ? 'text-blue-100' : 'text-gray-500'
            }`}
          >
            {formatTime(thread.last_message.created_at)}
          </span>
        )}
        {(thread.unread_count ?? 0) > 0 && (
          <span className="px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
            {(thread.unread_count ?? 0) > 99 ? '99+' : thread.unread_count}
          </span>
        )}
      </div>
    </button>
  );
};

export default DMThreadItem;
